import * as React from "react"
import { useTranslation } from "react-i18next"

import Table from "shared/components/Table"
import useEditor from "shared/hooks/useEditor"
import { DealLinePricing, IDeal, IDealLine } from "shared/hubrise/models"
import * as DealLine from "shared/hubrise/utils/DealLine"

import {
  FieldLabel,
  FieldNumber,
  FieldPricingEffect,
  FieldPricingValueMoney,
  FieldPricingValuePercentage,
} from "./Styles"

interface LinesProps {
  deal: IDeal
}

const Lines = ({ deal }: LinesProps): JSX.Element => {
  const { t } = useTranslation()
  const { editor, catalog } = useEditor()
  const { currency } = catalog

  const $newRowLabelRef = React.useRef<HTMLInputElement>(null)

  const handleChange =
    <TField extends keyof IDealLine>(
      dealLine: IDealLine,
      updateForNew: ((dealLine: IDealLine) => void) | undefined,
      field: TField,
    ) =>
    (value: IDealLine[TField]) =>
      updateForNew
        ? updateForNew({ ...dealLine, [field]: value })
        : editor.dealLine.update(deal.id, dealLine.id, field, value)

  const handleReorder = (newDealLines: Array<IDealLine>): void => {
    editor.deal.reorderLines(deal.id, newDealLines)
  }

  const handleDelete = (dealLine: IDealLine): void => {
    editor.dealLine.delete(deal.id, dealLine.id)
  }

  const canDelete = deal.lines.length >= 2

  const getNew = (): IDealLine => {
    setTimeout(() => $newRowLabelRef.current!.focus(), 0)
    return DealLine.create(catalog)
  }

  const tryCreate = (dealLine: IDealLine): boolean => {
    editor.dealLine.add(deal, dealLine)
    return true
  }

  return (
    <div>
      <Table
        items={deal.lines}
        columns={[
          {
            header: t("deal.lines.number"),
            cellRenderer: (dealLine, updateForNew) => (
              <FieldNumber>{DealLine.number(deal, dealLine, updateForNew !== undefined)}</FieldNumber>
            ),
          },
          {
            header: t("deal.lines.label"),
            cellRenderer: (dealLine, updateForNew) => (
              <FieldLabel
                ref={updateForNew ? $newRowLabelRef : undefined}
                initialValue={dealLine.label}
                onChange={handleChange(dealLine, updateForNew, "label")}
                debounce={updateForNew === undefined}
              />
            ),
          },
          {
            header: t("deal.lines.pricing_effect.label"),
            cellRenderer: (dealLine) => (
              <FieldPricingEffect
                initialValue={[dealLine.pricingEffect]}
                onChange={(value) => {
                  const pricingEffect = value[0] as DealLinePricing["pricingEffect"]
                  const pricing = DealLine.switchPricingEffect(dealLine, pricingEffect, currency)
                  editor.dealLine.updatePricing(deal.id, dealLine.id, pricing)
                }}
                options={[
                  { label: t("deal.lines.pricing_effect.fixed_price"), value: "fixed_price" },
                  { label: t("deal.lines.pricing_effect.percentage_off"), value: "percentage_off" },
                  { label: t("deal.lines.pricing_effect.price_off"), value: "price_off" },
                  { label: t("deal.lines.pricing_effect.unchanged"), value: "unchanged" },
                ]}
                debounce={false}
                searchable={false}
              />
            ),
          },
          {
            header: t("deal.lines.pricing_value"),
            cellRenderer: (dealLine, updateForNew) => {
              if (dealLine.pricingEffect === "fixed_price" || dealLine.pricingEffect === "price_off") {
                return (
                  <FieldPricingValueMoney
                    initialValue={dealLine.pricingValue}
                    onChange={handleChange(dealLine, updateForNew, "pricingValue")}
                    validate={(value) => (value === null ? t("validations.not_empty") : undefined)}
                    currency={currency}
                  />
                )
              } else if (dealLine.pricingEffect === "percentage_off") {
                return (
                  <FieldPricingValuePercentage
                    initialValue={dealLine.pricingValue.toString()}
                    onChange={(value) => handleChange(dealLine, updateForNew, "pricingValue")(Number(value))}
                    validate={(value: string) => (value.length === 0 ? t("validations.not_empty") : undefined)}
                    filter={/^\d*(\.\d{0,2})?$/}
                    prefix="%"
                  />
                )
              } else {
                return <></>
              }
            },
          },
        ]}
        onReorder={handleReorder}
        onDelete={canDelete ? handleDelete : undefined}
        droppableId="lines"
        addBehavior={{ buttonLabel: t("deal.lines.add"), getNew, tryCreate }}
      />
    </div>
  )
}

export default Lines
